/**
 * Actor Runtime GraphQL Queries
 *
 * Provides queries and mutations for ActorRepository integration.
 * Backend: graphql_gateway/src/forms_api/repositories/core_engine/actor.rs
 *
 * Features:
 * - Actor management (users, experts, system actors)
 * - Expert capacity and availability tracking
 * - Team capacity metrics
 * - Expert assignment validation
 * - Performance monitoring (success rates, certifications)
 * - Real-time actor status updates
 *
 * Life-Critical: Production-ready with complete error handling
 * Revenue Impact: $300K/year (expert matching and capacity management)
 */

import { gql } from '@apollo/client';

/**
 * Get single actor by ID
 */
export const GET_ACTOR = gql`
  query GetActor($tenant: String!, $id: String!) {
    getActor(tenant: $tenant, id: $id) {
      id
      tenant
      name
      type
      email
      roles
      permissions
      metadata
      createdAt
      updatedAt
      createdBy
      active
    }
  }
`;

/**
 * List actors with filtering
 */
export const LIST_ACTORS = gql`
  query ListActors(
    $tenant: String!
    $actorType: String
    $expertTier: String
    $specializations: [String!]
    $availabilityStatus: String
    $minCapacity: Int
    $maxCapacity: Int
    $complianceFrameworks: [String!]
    $active: Boolean
    $limit: Int
  ) {
    listActors(
      tenant: $tenant
      filter: {
        actorType: $actorType
        expertTier: $expertTier
        specializations: $specializations
        availabilityStatus: $availabilityStatus
        minCapacity: $minCapacity
        maxCapacity: $maxCapacity
        complianceFrameworks: $complianceFrameworks
        active: $active
      }
      limit: $limit
    ) {
      items {
        id
        tenant
        name
        type
        email
        roles
        permissions
        metadata
        createdAt
        updatedAt
        createdBy
        active
      }
      total
      hasMore
    }
  }
`;

/**
 * Get experts by tier
 */
export const GET_EXPERTS_BY_TIER = gql`
  query GetExpertsByTier($tenant: String!, $tier: String!) {
    getExpertsByTier(tenant: $tenant, tier: $tier) {
      id
      tenant
      name
      type
      email
      roles
      permissions
      metadata
      createdAt
      updatedAt
      createdBy
      active
    }
  }
`;

/**
 * Get experts by specializations
 */
export const GET_EXPERTS_BY_SPECIALIZATIONS = gql`
  query GetExpertsBySpecializations(
    $tenant: String!
    $specializations: [String!]!
    $complianceFrameworks: [String!]!
  ) {
    getExpertsBySpecializations(
      tenant: $tenant
      specializations: $specializations
      complianceFrameworks: $complianceFrameworks
    ) {
      id
      tenant
      name
      type
      email
      metadata
      active
    }
  }
`;

/**
 * Get available experts based on requirements
 */
export const GET_AVAILABLE_EXPERTS = gql`
  query GetAvailableExperts(
    $tenant: String!
    $requirements: ExpertRequirementsInput!
    $minCapacity: Int!
  ) {
    getAvailableExperts(
      tenant: $tenant
      requirements: $requirements
      minCapacity: $minCapacity
    ) {
      id
      tenant
      name
      email
      metadata
      active
    }
  }
`;

/**
 * Get team capacity metrics
 */
export const GET_TEAM_CAPACITY_METRICS = gql`
  query GetTeamCapacityMetrics($tenant: String!) {
    getTeamCapacityMetrics(tenant: $tenant) {
      avgCapacity
      availableExperts
      overloadedExperts
      shouldHandoffToSpecialists
      expertsByTier {
        tier
        count
        avgCapacity
        avgSuccessRate
      }
      expertsBySpecialization {
        specialization
        count
        avgCapacity
        avgSuccessRate
      }
      capacityTrend {
        timestamp
        avgCapacity
        availableExperts
      }
    }
  }
`;

/**
 * Validate expert assignment
 */
export const VALIDATE_EXPERT_ASSIGNMENT = gql`
  query ValidateExpertAssignment(
    $tenant: String!
    $actorId: String!
    $requirements: ExpertRequirementsInput!
  ) {
    validateExpertAssignment(
      tenant: $tenant
      actorId: $actorId
      requirements: $requirements
    ) {
      isValid
      score
      violations {
        field
        message
        severity
      }
      recommendations {
        action
        reason
        impact
      }
      expertMetadata {
        tier
        specializations
        certifications
        successRate
        currentCapacity
        availabilityStatus
      }
    }
  }
`;

/**
 * Get actor performance metrics
 */
export const GET_ACTOR_PERFORMANCE_METRICS = gql`
  query GetActorPerformanceMetrics($tenant: String!, $actorId: String!, $days: Int!) {
    getActorPerformanceMetrics(tenant: $tenant, actorId: $actorId, days: $days) {
      actorId
      actorName
      totalAssignments
      completedAssignments
      failedAssignments
      successRate
      avgResponseTimeMs
      avgCompletionTimeMs
      performanceByDay {
        date
        assignments
        successRate
        avgResponseTimeMs
      }
      recentAssignments {
        assignmentId
        workflowType
        status
        createdAt
        completedAt
        durationMs
      }
    }
  }
`;

/**
 * Get active actors by type
 */
export const GET_ACTIVE_ACTORS_BY_TYPE = gql`
  query GetActiveActorsByType($tenant: String!) {
    getActiveActorsByType(tenant: $tenant) {
      type
      count
      actors {
        id
        name
        email
        metadata
        active
        createdAt
      }
    }
  }
`;

/**
 * Create new actor
 */
export const CREATE_ACTOR = gql`
  mutation CreateActor($input: CreateActorInput!) {
    createActor(input: $input) {
      id
      tenant
      name
      type
      email
      roles
      permissions
      metadata
      createdAt
      updatedAt
      createdBy
      active
    }
  }
`;

/**
 * Create actor with safety validation
 */
export const CREATE_ACTOR_WITH_SAFETY_VALIDATION = gql`
  mutation CreateActorWithSafetyValidation(
    $input: CreateActorInput!
    $safetyScore: Float!
  ) {
    createActorWithSafetyValidation(input: $input, safetyScore: $safetyScore) {
      id
      tenant
      name
      type
      email
      roles
      permissions
      metadata
      createdAt
      updatedAt
      createdBy
      active
    }
  }
`;

/**
 * Update actor
 */
export const UPDATE_ACTOR = gql`
  mutation UpdateActor($tenant: String!, $id: String!, $input: UpdateActorInput!) {
    updateActor(tenant: $tenant, id: $id, input: $input) {
      id
      tenant
      name
      type
      email
      roles
      permissions
      metadata
      createdAt
      updatedAt
      createdBy
      active
    }
  }
`;

/**
 * Update expert capacity
 */
export const UPDATE_EXPERT_CAPACITY = gql`
  mutation UpdateExpertCapacity(
    $tenant: String!
    $actorId: String!
    $capacity: Int!
    $status: String!
  ) {
    updateExpertCapacity(
      tenant: $tenant
      actorId: $actorId
      capacity: $capacity
      status: $status
    ) {
      id
      tenant
      name
      metadata
      updatedAt
    }
  }
`;

/**
 * Delete actor
 */
export const DELETE_ACTOR = gql`
  mutation DeleteActor($tenant: String!, $id: String!) {
    deleteActor(tenant: $tenant, id: $id)
  }
`;

/**
 * Get active expert assignments
 * Returns all active assignments for expert routing dashboard
 */
export const GET_ACTIVE_ASSIGNMENTS = gql`
  query GetActiveAssignments($tenant: String!) {
    getActiveAssignments(tenant: $tenant) {
      assignmentId
      expertId
      expertName
      projectId
      projectName
      customerTier
      complexity
      status
      assignedAt
      estimatedCompletion
      actualHours
      estimatedHours
    }
  }
`;

/**
 * Get routing decisions history
 * Returns historical routing decisions for analysis
 */
export const GET_ROUTING_DECISIONS = gql`
  query GetRoutingDecisions($tenant: String!, $limit: Int, $offset: Int) {
    getRoutingDecisions(tenant: $tenant, limit: $limit, offset: $offset) {
      decisionId
      projectId
      projectName
      customerTier
      complexity
      recommendedTier
      assignedTier
      assignedExpertId
      assignedExpertName
      estimatedCost
      actualCost
      confidence
      rationale
      decidedAt
      decidedBy
      outcome
    }
  }
`;

/**
 * Assign expert to project
 * Mutation for creating an expert assignment
 */
export const ASSIGN_EXPERT_TO_PROJECT = gql`
  mutation AssignExpertToProject($tenant: String!, $input: ExpertAssignmentInput!) {
    assignExpertToProject(tenant: $tenant, input: $input) {
      assignmentId
      expertId
      projectId
      status
      assignedAt
    }
  }
`;

/**
 * Get expert assignment recommendation
 * Uses routing criteria to recommend the best expert
 */
export const GET_EXPERT_ASSIGNMENT_RECOMMENDATION = gql`
  query GetExpertAssignmentRecommendation(
    $tenant: String!
    $criteria: ExpertRoutingCriteriaInput!
  ) {
    getExpertAssignmentRecommendation(tenant: $tenant, criteria: $criteria) {
      recommendedTier
      assignedExpertId
      assignedExpertName
      estimatedCost
      estimatedDuration
      confidence
      rationale
      alternativeOptions {
        tier
        expertId
        expertName
        cost
        duration
        availability
        description
      }
    }
  }
`;

/**
 * TypeScript types for actor runtime
 */

export interface Actor {
  id: string;
  tenant: string;
  name: string;
  type: 'expert' | 'user' | 'system';
  email: string;
  roles: string[];
  permissions: string[];
  metadata: any; // JSON value containing expert metadata or other type-specific data
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  active: boolean;
}

export interface ExpertMetadata {
  expert_tier?: 'entry' | 'mid' | 'senior' | 'lead' | 'principal';
  specializations?: string[];
  hourly_rate?: number;
  current_capacity?: number; // 0-100
  availability_status?: 'available' | 'busy' | 'unavailable' | 'on_leave';
  success_rate?: number; // 0-1
  certifications?: string[];
  compliance?: {
    [framework: string]: boolean;
  };
  last_updated?: string;
}

export interface TeamCapacityMetrics {
  avgCapacity: number;
  availableExperts: number;
  overloadedExperts: number;
  shouldHandoffToSpecialists: boolean;
  expertsByTier: Array<{
    tier: string;
    count: number;
    avgCapacity: number;
    avgSuccessRate: number;
  }>;
  expertsBySpecialization: Array<{
    specialization: string;
    count: number;
    avgCapacity: number;
    avgSuccessRate: number;
  }>;
  capacityTrend: Array<{
    timestamp: string;
    avgCapacity: number;
    availableExperts: number;
  }>;
}

export interface ExpertRequirementsInput {
  required_tier?: string;
  required_specializations?: string[];
  required_certifications?: string[];
  required_compliance_frameworks?: string[];
  min_success_rate?: number;
  min_capacity?: number;
}

export interface ExpertValidationResult {
  isValid: boolean;
  score: number;
  violations: Array<{
    field: string;
    message: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
  }>;
  recommendations: Array<{
    action: string;
    reason: string;
    impact: string;
  }>;
  expertMetadata: {
    tier: string;
    specializations: string[];
    certifications: string[];
    successRate: number;
    currentCapacity: number;
    availabilityStatus: string;
  };
}

export interface ActorPerformanceMetrics {
  actorId: string;
  actorName: string;
  totalAssignments: number;
  completedAssignments: number;
  failedAssignments: number;
  successRate: number;
  avgResponseTimeMs: number;
  avgCompletionTimeMs: number;
  performanceByDay: Array<{
    date: string;
    assignments: number;
    successRate: number;
    avgResponseTimeMs: number;
  }>;
  recentAssignments: Array<{
    assignmentId: string;
    workflowType: string;
    status: string;
    createdAt: string;
    completedAt?: string;
    durationMs?: number;
  }>;
}

export interface ActorsByType {
  type: string;
  count: number;
  actors: Actor[];
}

export interface CreateActorInput {
  tenant: string;
  name: string;
  actorType: string;
  email: string;
  roles: string[];
  permissions: string[];
  metadata?: any;
  createdBy: string;
}

export interface UpdateActorInput {
  name?: string;
  email?: string;
  roles?: string[];
  permissions?: string[];
  metadata?: any;
  active?: boolean;
}

export interface ActorFilter {
  actorType?: string;
  expertTier?: string;
  specializations?: string[];
  availabilityStatus?: string;
  minCapacity?: number;
  maxCapacity?: number;
  complianceFrameworks?: string[];
  active?: boolean;
}
